import * as React from 'react'
import { lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Pencil } from '@factorialco/f0-react/icons/app'
import { TeamDocument, TeamsTabsDocument } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

const AddMembersModal = lazy(() => import('modules/teams/components/TeamModal/AddMembersModal'))
const DeleteTeamModal = lazy(() => import('modules/teams/components/TeamModal/DeleteTeamModal'))
const EditTeamModal = lazy(() => import('modules/teams/components/TeamModal/EditTeamModal'))

const RootPage = React.lazy(() => import('modules/teams/pages/RootPage'))

const NewJobRoleModalPage = React.lazy(
  () => import('modules/job_catalog/pages/NewJobRoleModalPage')
)

const PeopleChartPage = React.lazy(() => import('modules/people_chart/pages/PeopleChartPage'))

const NewTeamPage = React.lazy(() => import('modules/teams/pages/NewTeamPage'))
const RoleCreatedModalPage = React.lazy(() => import('modules/teams/pages/RoleCreatedModalPage'))
const UniversalImporterTeamsPage = React.lazy(
  () => import('modules/teams/pages/UniversalImporterTeamsPage')
)

const TeamDetailPage = React.lazy(() => import('modules/teams/pages/TeamDetailPage'))
const TeamsList = React.lazy(() => import('modules/teams/pages/TeamsListPage'))

export const teamsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.employees.teams.path,
      element: <RootPage />,
      handle: {
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: TeamsTabsDocument,
            variables: {
              companyId: session.company.id,
            },
          }).map(({ data }) => {
            const canSeeTeamsChart = data?.features.featuresConnection.nodes[0]?.enabled ?? false
            return {
              title: i18n.t('teams.title'),
              subNavigation: [
                {
                  title: i18n.t('teams.list'),
                  href: navigation.employees.teams.list.fullPath(),
                },
                {
                  title: i18n.t('teams.chart'),
                  href: navigation.employees.teams.chart.fullPath(),
                  isVisible: canSeeTeamsChart,
                },
              ],
            }
          })
        },
      },

      children: [
        {
          index: true,
          element: <Navigate replace to={navigation.employees.teams.list.path} />,
        },
        {
          path: navigation.employees.teams.list.path,
          element: <TeamsList />,
          children: [
            {
              path: navigation.employees.teams.list.add.path,
              element: <NewTeamPage />,
            },
          ],
        },
        {
          path: '/employees/teams/import-teams',
          element: (
            <Routes>
              <Route path="" element={<UniversalImporterTeamsPage />} />
            </Routes>
          ),
          handle: {
            resource: true,
            meta: () => ({
              title: i18n.t('teams.import_teams.title'),
              headerComponent: (
                <ResourceHeader
                  title={i18n.t('teams.import_teams.title')}
                  description={i18n.t('teams.import_teams.description')}
                />
              ),
            }),
          },
        },
        {
          path: navigation.employees.teams.id.path,
          element: <TeamDetailPage />,
          handle: {
            resource: true,
            meta: ({ params, watchQuery, session }) => {
              const id = Number(params['id'])
              const companyId = session.company.id

              return watchQuery({
                query: TeamDocument,
                fetchPolicy: 'cache-and-network',
                variables: {
                  teamId: id,
                  companyId,
                },
              }).map(({ data }) => {
                const team = data.teams.team

                if (!team) {
                  return { title: '' }
                }

                const canManageTeams = team._policies?.manage?.can ?? false
                return {
                  headerComponentProps: {
                    title: team.name,
                    description: team.description || '',
                    avatar: {
                      name: team.name,
                      src: buildUrl(team.avatar),
                      type: 'team',
                    },
                    primaryAction: canManageTeams
                      ? {
                          key: 'add-team',
                          label: i18n.t('teams.members_modal.title'),
                          icon: Add,
                        }
                      : undefined,
                    secondaryActions: canManageTeams
                      ? [
                          {
                            key: 'edit-team',
                            label: i18n.t('common.edit'),
                            icon: Pencil,
                          },
                          {
                            key: 'delete-team',
                            label: i18n.t('common.delete'),
                            variant: 'critical',
                            icon: Delete,
                          },
                        ]
                      : [],
                  },
                  title: team?.name || 'no name',
                }
              })
            },
          },
          children: [
            {
              path: navigation.employees.teams.id.addMembers.path,
              element: <AddMembersModal />,
            },
            { path: navigation.employees.teams.id.edit.path, element: <EditTeamModal /> },
            {
              path: navigation.employees.teams.id.delete.path,
              element: <DeleteTeamModal />,
            },
            {
              path: navigation.employees.teams.id.addJobCatalogRole.path,
              element: <NewJobRoleModalPage />,
            },
            {
              path: navigation.employees.teams.id.jobCatalogRoleCreated.path,
              element: <RoleCreatedModalPage />,
            },
          ],
        },
        {
          path: navigation.employees.teams.chart.path,
          element: <PeopleChartPage />,
        },
      ],
    },
  ]
}
